import React, { useCallback } from 'react';
import { Venus, Mars, RotateCcw } from 'lucide-react';
import { ToggleGroup, ToggleGroupItem } from './ui/toggle-group';
import { Button } from './ui/button';
import { useTracking, UI_EVENTS } from '../hooks/useTracking';
import { cn } from '@/lib/utils';

interface CharacterFiltersProps {
  gender: string;
  setGender: (v: string) => void;
  style: string;
  setStyle: (v: string) => void;
  activeTag: string;
  setActiveTag: (v: string) => void;
  // nsfw: boolean;
  // setNsfw: (v: boolean) => void;
  // search: string;
  // setSearch: (v: string) => void;
}

export const ALL_TAG = 'ALL';

const filterTags = [
  // 'Official', 'Community', ALL_TAG,
  'Girlfriend', 'Boss', 'Colleague', 'Mistress', 'Wife',
  'Asian', 'Black', 'Hispanic', 'Indian', 'White', 'Middle-Eastern',
   'Amusing', 'Caregiver', 'Dominant',  'Insatiable', 'Romantic', 'Seducer'
];

const CharacterFilters: React.FC<CharacterFiltersProps> = ({
  gender, setGender,
  style, setStyle,
  activeTag, setActiveTag,
  // nsfw, setNsfw,
  // search, setSearch
}) => {
  const { trackEvent } = useTracking();

  const handleGenderChange = useCallback((v: string) => {
    setGender(v || '');
    if (v) trackEvent(UI_EVENTS.CHARACTER_FILTER_CLICKED, { filter: 'gender', value: v });
  }, [setGender, trackEvent]);

  const handleStyleChange = useCallback((v: string) => {
    setStyle(v || '');
    if (v) trackEvent(UI_EVENTS.CHARACTER_FILTER_CLICKED, { filter: 'style', value: v });
  }, [setStyle, trackEvent]);

  const handleTagChange = useCallback((v: string) => {
    setActiveTag(v || ALL_TAG);
    if (v) trackEvent(UI_EVENTS.CHARACTER_FILTER_CLICKED, { filter: 'tag', value: v });
  }, [setActiveTag, trackEvent]);

  // Reset all filters
  const handleReset = useCallback(() => {
    setGender('');
    setStyle('');
    setActiveTag(ALL_TAG);
    // setSearch('');
    trackEvent(UI_EVENTS.CHARACTER_FILTER_CLICKED, { filter: 'reset' });
  }, [setGender, setStyle, setActiveTag/*, setSearch*/, trackEvent]);

  return (
    <section className="w-full px-4 py-4 bg-zinc-900 border-b border-zinc-800 flex flex-col gap-3">
      <div className="flex flex-wrap gap-3 items-center">
        {/* Gender ToggleGroup */}
        <ToggleGroup type="single" value={gender} onValueChange={handleGenderChange}
          className="[&_[data-state=on]]:bg-pink-700 [&_[data-state=on]]:text-white [&_[data-state=on]:hover]:bg-pink-800"
        >
          <ToggleGroupItem
            value="f"
            aria-label="Female"
            className={`flex items-center justify-center text-xl font-medium size-10 ${gender === 'f' ? 'bg-pink-700 text-white' : 'bg-zinc-800 text-pink-400 hover:bg-pink-800'}`}
          >
            <Venus className="size-6" />
          </ToggleGroupItem>
          <ToggleGroupItem
            value="m"
            aria-label="Male"
            className={`flex items-center justify-center text-xl font-medium size-10 ${gender === 'm' ? 'bg-pink-700 text-white' : 'bg-zinc-800 text-blue-300 hover:bg-pink-800'}`}
          >
            <Mars className="size-6" />
          </ToggleGroupItem>
        </ToggleGroup>
        {/* Style ToggleGroup */}
        <ToggleGroup type="single" value={style} onValueChange={handleStyleChange}
          className="[&_[data-state=on]]:bg-pink-700 [&_[data-state=on]]:text-white [&_[data-state=on]:hover]:bg-pink-800"
        >
          <ToggleGroupItem
            value="realistic"
            className={`px-4 py-1 text-base text-zinc-200 ${style === 'realistic' ? 'bg-pink-700' : 'bg-zinc-800 hover:bg-pink-800'}`}
          >
            Realistic
          </ToggleGroupItem>
          <ToggleGroupItem
            value="anime"
            className={`px-4 py-1 text-base text-zinc-200 ${style === 'anime' ? 'bg-pink-700' : 'bg-zinc-800 hover:bg-pink-800'}`}
          >
            Anime
          </ToggleGroupItem>
        </ToggleGroup>
        {/* NSFW Toggle with Switch */}
        {/* <button 
          className={`flex items-center gap-2 h-10 px-5 py-1 text-base rounded ${
            nsfw ? 'bg-pink-700 text-white' : 'bg-zinc-800 text-zinc-200 hover:bg-pink-800'
          }`}
          onClick={() => setNsfw(!nsfw)}
          type="button"
        >     
          NSFW */}
          {/* <Switch
            checked={nsfw}
            onClick={e => { e.stopPropagation(); e.preventDefault(); }}
          /> */}
        {/* </button> */}
        {/* Search */}
        {/* TODO: trigram search for typos and more results */}
        {/* <input
          className="flex-[1_0_auto] px-4 py-2 rounded bg-zinc-700 text-white placeholder:text-zinc-400 max-w-full lg:max-w-3xl focus:outline-none focus:ring-2 focus:ring-pink-700 transition"
          placeholder="Search characters, kinks, personalities, and more!"
          value={search}
          onChange={e => setSearch(e.target.value)}
        /> */}
        {/* Reset Filters Button */}
        <Button
          type="button"
          variant="secondary"
          size="sm"
          className="flex items-center gap-2 h-10 px-4 py-1 text-base rounded bg-zinc-800 text-zinc-200 hover:bg-pink-800"
          onClick={handleReset}
        >
          <RotateCcw className="size-4" />
          <span className="hidden md:block">Reset Filters</span>
        </Button>
      </div>
      {/* Tag ToggleGroup */}
      <ToggleGroup
        type="single" value={activeTag}
        onValueChange={handleTagChange}
        className={cn(
          'flex lg:flex-wrap gap-2 items-center [&_[data-state=on]]:bg-pink-700 [&_[data-state=on]]:text-white [&_[data-state=on]:hover]:bg-pink-800',
          'max-w-full overflow-y-auto overflow-x-auto snap-x snap-mandatory'
        )}
        style={{ scrollbarWidth: 'thin' }}
      >
        {filterTags.map((tag) => (
          <ToggleGroupItem
            key={tag}
            value={tag}
            className={cn(
              `flex-[0_0_auto] px-3 py-1 text-sm rounded font-medium text-zinc-200`,
              activeTag === tag ? 'bg-pink-700' : 'bg-zinc-800 hover:bg-pink-800',
              ['Colleague'].includes(tag) && 'hidden lg:flex'
            )}
          >
            {tag}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
    </section>
  );
};

export default CharacterFilters; 